import {
  provedorOpencodeNaoTratado,
  VERSAO_AGENTE_NANOGPT,
  VERSAO_AGENTE_OPENCODE,
  type ProvedorOpencode,
} from '@remoto/protocolo';

/**
 * O provedor dos modelos do OpenCode visto do lado da casa (F2 da revisão de
 * out/2026, ADR-0025). O desenho está em `medicoes/F2-desenho.md` do plano, e
 * cada escolha daqui foi MEDIDA no binário 1.18.32 antes de ser escrita.
 *
 * **O id do modelo carrega o provedor**, sem migration nenhuma:
 * - NanoGPT: `nano-gpt/<id do NanoGPT>` — o id do NanoGPT às vezes tem barra
 *   (`z-ai/glm-5.3-flash-uncensored`), às vezes não (`GLM-4.6-Derestricted-v5`);
 * - OpenRouter: o id de sempre, sem prefixo (`deepseek/deepseek-v4.1-flash`).
 *
 * Toda linha já gravada (sessões, configuração) continua dizendo a mesma
 * coisa, e o `IdModeloOpencodeSchema` (`provedor/modelo`) vale para as duas
 * formas. Não há ambiguidade: nenhum fornecedor do OpenRouter se chama
 * `nano-gpt`.
 */

/** O provedor que a frota usa quando ninguém escolheu outro (D11). */
export const PROVEDOR_PADRAO_OPENCODE: ProvedorOpencode = 'nanogpt';

/** O prefixo dos ids da casa que são do NanoGPT: o `providerID` dele no OpenCode, mais a barra. */
export const PREFIXO_DO_NANOGPT = 'nano-gpt/';

/**
 * O host DIRETO do NanoGPT, que a camada gerenciada fixa e a API lê.
 *
 * A documentação dele o recomenda para chave de API (corpo de 32 MiB e tempo
 * de execução maior que o do site, que roda na Vercel) — e turno longo de
 * agente é o caso. Medido em 26/10: o OpenCode funciona com ele. Os catálogos
 * dos dois hosts divergem durante as implantações deles (de manhã o Gemma
 * Chimera era `google/…` aqui e sem prefixo no site; à tarde, igual nos dois),
 * então a API e o `serve` leem o MESMO.
 */
export const BASE_URL_DO_NANOGPT = 'https://api.nano-gpt.com/api/v1';

/** O `providerID` de cada provedor no OpenCode (o id do models.dev). */
export function idDoProvedorNoOpencode(provedor: ProvedorOpencode): 'nano-gpt' | 'openrouter' {
  switch (provedor) {
    case 'nanogpt':
      return 'nano-gpt';
    case 'openrouter':
      return 'openrouter';
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/** De quem é um id de modelo da casa. */
export function provedorDoModelo(id: string): ProvedorOpencode {
  return id.startsWith(PREFIXO_DO_NANOGPT) ? 'nanogpt' : 'openrouter';
}

/**
 * O id da casa como o OpenCode quer no prompt: `{providerID, modelID}`.
 * O `modelID` pode ter barra (medido: o OpenCode aceita as duas formas).
 */
export function modeloNoOpencode(id: string): { providerID: 'nano-gpt' | 'openrouter'; modelID: string } {
  const provedor = provedorDoModelo(id);
  switch (provedor) {
    case 'nanogpt':
      return { providerID: 'nano-gpt', modelID: id.slice(PREFIXO_DO_NANOGPT.length) };
    case 'openrouter':
      return { providerID: 'openrouter', modelID: id };
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/** O id da casa a partir do id do NanoGPT (o do catálogo dele). */
export function idDaCasaDoNanogpt(idNoNanogpt: string): string {
  return PREFIXO_DO_NANOGPT + idNoNanogpt;
}

/**
 * O primeiro daemon que fala cada provedor (F2): o OpenRouter desde o OpenCode
 * (0.7.0), o NanoGPT desde o 0.8.0. Abaixo disso a API manda `chave: null` e a
 * máquina recusa sessão nova — e a tela tem de dizer "daemon desatualizado",
 * não "falta a chave", que mandaria o dono colar a chave de novo.
 *
 * Mora aqui para a tela de criar e a API lerem a MESMA régua; a API ainda tem a
 * sua (`daemonFalaOProvedor`, em `configuracoes/credencial.regras.ts`), escrita
 * sobre as mesmas duas constantes.
 */
export function versaoDoDaemonQueFala(provedor: ProvedorOpencode): string {
  switch (provedor) {
    case 'nanogpt':
      return VERSAO_AGENTE_NANOGPT;
    case 'openrouter':
      return VERSAO_AGENTE_OPENCODE;
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/** O nome do provedor para a tela e para os textos da sessão (o selo, a confirmação). */
export function nomeDoProvedorOpencode(provedor: ProvedorOpencode): string {
  switch (provedor) {
    case 'nanogpt':
      return 'NanoGPT';
    case 'openrouter':
      return 'OpenRouter';
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}
